import PeriodReportAttachment from "../../../models/accounting/periodReport/PeriodReportAttachment.js";
import PeriodReportPDFExportService from "./pdfExport/PeriodReportPDFExportService.js";
import UploadService from "../../../core/services/UploadService.js";

/**
 * `PeriodReportAttachmentsService` helps updating the default period report attachments.
 *
 * @param {UploadService} [spec.uploadService] - class used to save the pdf files.
 */
export default class PeriodReportAttachmentsService {
	constructor({ uploadService = new UploadService() } = {}) {
		this._uploadService = uploadService;
	}

	reportHasAllDefaultAttachments(report) {
		const exportService = new PeriodReportPDFExportService({
			periodReport: report,
		});

		return exportService.reportHasAllDefaultAttachments();
	}

	/**
	 * Add attachments based on report templates referenced by their ids
	 *
	 * @param {PeriodReportDraft} draft - draft of the period report
	 * @param {ChartOfAccounts} chartOfAccounts - chart of accounts of the current period
	 * @param {KeyFigure[]} [keyFigures] - keyFigures in the period report
	 * @param {string[]} reportTemplateIds - report templates to add as attachments
	 */
	async addReportTemplatesAttachments({
		draft,
		chartOfAccounts,
		keyFigures,
		reportTemplateIds,
	}) {
		const attachments = await this._updateDraftAttachments({
			draft,
			chartOfAccounts,
			keyFigures,
			reportTemplateIds,
		});

		draft.addAttachments(attachments);
	}

	async updateDraftDefaultAttachments({
		draft,
		chartOfAccounts,
		keyFigures,
	}) {
		const attachments = await this._updateDraftAttachments({
			draft,
			chartOfAccounts,
			keyFigures,
		});

		draft.updateDefaultAttachments(attachments);
	}

	/**
	 * Generate PDF for the provided report template ids and executes an attachment action on the draft
	 *
	 * @param {PeriodReportDraft} draft - Draft of the period report
	 * @param {ChartOfAccounts} chartOfAccounts - Chart of accounts for the current period
	 * @param {KeyFigure[]} keyFigures - Key figures for the period report
	 * @param {string[]} [reportTemplateIds] - report templates to add as attachments. If undefined, we are updating default attachments
	 * @private
	 */
	async _updateDraftAttachments({
		draft,
		chartOfAccounts,
		keyFigures,
		reportTemplateIds,
	}) {
		const exportService = new PeriodReportPDFExportService({
			periodReport: draft,
			chartOfAccounts,
			keyFigures,
		});

		const pdfs = await exportService.buildReportAttachments({
			reportTemplateIds,
		});

		return Promise.all(
			pdfs.map((pdf) =>
				this._makeAttachment({
					pdf,
					draft,
					isDefaultAttachment: !reportTemplateIds,
				}),
			),
		);
	}

	async _makeAttachment({ pdf, draft, isDefaultAttachment }) {
		const file = await this._makeFile({
			pdf,
			period: draft.getPeriod().toSmartString(),
		});

		return this._uploadFile({ file, isDefaultAttachment });
	}

	async _uploadFile({ file, isDefaultAttachment }) {
		const [{ id, fileName }] = await this._uploadService.uploadFile(file);

		return new PeriodReportAttachment({
			id,
			filename: fileName,
			isDefaultAttachment,
		});
	}

	async _makeFile({ pdf, period }) {
		const buffer = await pdf.getBuffer();
		const filename = `${pdf.title} - ${period}.pdf`;
		return this._createPayload(buffer, filename);
	}

	_createPayload(buffer, filename) {
		// Only works for IE10 and up, including Edge
		return navigator.msSaveBlob
			? this._createBlob(buffer, filename)
			: this._createFile(buffer, filename);
	}

	_createBlob(buffer, filename) {
		const blob = new Blob([buffer], { type: "application/pdf" });
		blob.name = filename;
		blob.lastModifiedDate = new Date();
		return blob;
	}

	_createFile(buffer, filename) {
		return new File([buffer], filename);
	}
}
